import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Paper,
  TablePagination,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from "react-redux";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { exportStyledExcel, pdfTableTheme } from '../utils/exportHelpers';
import { waktuCabang } from '../utils/waktuCabang';
import { apiHost } from '../utils/apiHost';

// Utils
const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};
const fetcher = (url) => axios.get(url, { withCredentials: true }).then(res => res.data);

// Komponen untuk keadaan kosong
const TableEmptyState = ({ colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="subtitle1" color="text.secondary">
        Tidak ada data mutasi ditemukan untuk periode ini.
      </Typography>
    </TableCell>
  </TableRow>
);

// Komponen untuk error state
const TableErrorState = ({ message }) => (
  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
    <Typography variant="h6" color="error">
      Error: {message || "Gagal memuat data mutasi."}
    </Typography>
    <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
      Coba Lagi
    </Button>
  </Box>
);

export const Mutasi = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useSelector((state) => state.auth);

  const endpoint = `${getApiBaseUrl()}${user?.role === "superadmin" ? "/getmutasi" : "/getmutasitracking"}?startDate=${startDate}&endDate=${endDate}&page=${page + 1}&limit=${rowsPerPage}`;
  const { data, error, isLoading } = useSWR(endpoint, fetcher);

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setPage(0);
  };
  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setPage(0);
  };
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Mutasi Stok", 14, 10);
    autoTable(doc, {
      startY: 20,
      head: [['Nama Barang', 'Cabang', 'Jenis Mutasi', 'Jumlah', 'Dibuat', 'Diperbarui', 'Keterangan']],
      body: data?.data?.map(item => [
        item.Barang?.namabarang,
        item.Cabang?.namacabang || '-',
        item.jenis_mutasi,
        item.jumlah,
        dayjs(waktuCabang(item.createdAt)).format('DD/MM/YYYY HH:mm'),
        dayjs(waktuCabang(item.updatedAt)).format('DD/MM/YYYY HH:mm'),
        item.keterangan,
      ]),
      ...pdfTableTheme,
    });
    doc.save("mutasi_stok.pdf");
  };

  const exportExcel = async () => {
    const rows = (data?.data || []).map(item => [
      item.Barang?.namabarang,
      item.Cabang?.namacabang || '-',
      item.jenis_mutasi,
      item.jumlah,
      dayjs(waktuCabang(item.createdAt)).format('DD/MM/YYYY HH:mm'),
      dayjs(waktuCabang(item.updatedAt)).format('DD/MM/YYYY HH:mm'),
      item.keterangan,
    ]);
    await exportStyledExcel({
      fileName: 'mutasi_stok.xlsx',
      sheetName: 'Mutasi Stok',
      title: 'Mutasi Stok',
      metaRows: [['Periode', `${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`]],
      headers: ['Nama Barang', 'Cabang', 'Jenis Mutasi', 'Jumlah', 'Dibuat', 'Diperbarui', 'Keterangan'],
      rows,
    });
  };

  // Error Handling
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Memuat data mutasi...</Typography>
      </Box>
    );
  }

if (error && error.response?.status !== 404) {
  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <Typography variant="h6" color="error">
        Error: {error.message || "Gagal memuat data mutasi."}
      </Typography>
      <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
        Coba Lagi
      </Button>
    </Box>
  );
}
  const hasData = data?.data?.length > 0;

  return (
    <Card>
      <CardHeader
        title="Mutasi Stok"
        action={
          <Box display="flex" gap={2}>
            <TextField label="Start Date" type="date" value={startDate} onChange={handleStartDateChange} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" value={endDate} onChange={handleEndDateChange} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" color="primary" onClick={exportPDF} disabled={!hasData}>Export PDF</Button>
            <Button variant="contained" color="success" onClick={exportExcel} disabled={!hasData}>Export Excel</Button>
          </Box>
        }
      />
      <Divider />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nama Barang</TableCell>
              <TableCell>Cabang</TableCell>
              <TableCell>Jenis Mutasi</TableCell>
              <TableCell>Jumlah</TableCell>
              <TableCell>Dibuat</TableCell>
              <TableCell>Diperbarui</TableCell>
              <TableCell>Keterangan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasData ? (
              <TableEmptyState colSpan={7} />
            ) : (
              data.data.map((item) => (
                <TableRow key={item.uuid}>
                  <TableCell>{item.Barang?.namabarang}</TableCell>
                  <TableCell>{item.Cabang?.namacabang || '-'}</TableCell>
                  <TableCell>
                    <Chip label={item.jenis_mutasi} color={item.jenis_mutasi === 'masuk' ? 'success' : 'error'} size="small" />
                  </TableCell>
                  <TableCell>{item.jumlah}</TableCell>
                  <TableCell>{dayjs(waktuCabang(item.createdAt)).format('DD/MM/YYYY HH:mm')}</TableCell>
                  <TableCell>{dayjs(waktuCabang(item.updatedAt)).format('DD/MM/YYYY HH:mm')}</TableCell>
                  <TableCell>{item.keterangan}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={data?.pagination?.totalItems || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Card>
  );
};

export default Mutasi;
